import type { ColumnDef, TableOptions } from "@tanstack/react-table";

import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { download, generateCsv, mkConfig } from "export-to-csv";

import type { ButtonSize } from "../Button";

import { ExportCSVButton } from "../Button/ExportCSVButton";
import { Typo } from "../Typo";

export type ExportCSVProps<T> = {
  buttonLabel: string;
  data: T[];
  columns: ColumnDef<T, any>[];
  globalFilter?: any;
  columnFilters?: any;
  dataFormatter: (record: T) => any;
  sorting?: any;
  onClick?: () => void;
  onExport?: () => void | Promise<void>;
  isExporting?: boolean;
  fileName: string;
  showIcon?: boolean;
  size?: ButtonSize;
} & Pick<TableOptions<T>, "filterFns">;

export const ExportCSV = <T,>({
  buttonLabel,
  data,
  columns,
  globalFilter,
  columnFilters,
  dataFormatter,
  sorting,
  onClick,
  onExport,
  isExporting = false,
  fileName,
  showIcon,
  size = "md",
  ...filterProps
}: ExportCSVProps<T>) => {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      globalFilter,
      sorting,
      columnFilters,
    },
    ...filterProps,
  });

  const filteredData = table
    .getPrePaginationRowModel()
    .rows.map((row) => dataFormatter(row.original));

  const csvConfig = mkConfig({
    useKeysAsHeaders: true,
    filename: fileName,
  });

  const handleClick = () => {
    if (onExport) {
      onClick?.();
      void onExport();
      return;
    }

    if (!filteredData || filteredData.length === 0) {
      return;
    }

    const csv = generateCsv(csvConfig)(filteredData);

    download(csvConfig)(csv);

    onClick?.();
  };

  return (
    <ExportCSVButton
      size={size}
      onClick={handleClick}
      isLoading={isExporting}
      showIcon={showIcon}
      label={
        <Typo element="span" lgType="labelSM" smType="labelXS">
          {buttonLabel}
        </Typo>
      }
    />
  );
};
